import {
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  useTheme,
} from '@mui/material';
import { Close, ErrorOutline, FormatAlignJustify } from '@mui/icons-material';
import React, { useState } from 'react';
import { Draggable } from '@hello-pangea/dnd';

import ListItemButton from '@mui/material/ListItemButton';

export enum AccountFileListItemRightButtonType {
  LOADING,
  CLOSE,
  SAVE_ERROR,
}

export interface AccountFileListItemProps {
  accountFileId: string;
  accountName: string;
  selected: boolean;
  index: number;
  onClick: () => void;

  closeButtonType: AccountFileListItemRightButtonType;
  onCloseButtonClicked: () => void;
}

const accountTextSx = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
  whiteSpace: 'nowrap',
} as const;

export const AccountFileListItem = (props: AccountFileListItemProps) => {
  const {
    accountFileId,
    accountName,
    closeButtonType,
    index,
    onClick,
    onCloseButtonClicked,
    selected,
  } = props;

  const theme = useTheme();

  const [isHovered, setHovered] = useState(false);

  const showLoading =
    closeButtonType === AccountFileListItemRightButtonType.LOADING;
  const showCloseButton =
    selected &&
    (closeButtonType === AccountFileListItemRightButtonType.CLOSE ||
      (isHovered &&
        closeButtonType === AccountFileListItemRightButtonType.SAVE_ERROR));
  const showSaveError =
    closeButtonType === AccountFileListItemRightButtonType.SAVE_ERROR &&
    (!selected || !isHovered);

  const rightButtonDisplayed =
    showLoading ||
    closeButtonType === AccountFileListItemRightButtonType.SAVE_ERROR ||
    (selected && closeButtonType === AccountFileListItemRightButtonType.CLOSE);

  return (
    <Draggable
      draggableId={'accountFileListItem$' + accountFileId}
      index={index}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            ...provided.draggableProps.style,
            backgroundColor: snapshot.isDragging
              ? theme.palette.background.paper
              : undefined,
          }}
        >
          <ListItemButton
            sx={[
              { borderRight: '6px solid transparent' },
              selected && {
                borderRight: `6px solid ${theme.palette.secondary.main}`,
              },
            ]}
            selected={selected}
            onClick={onClick}
          >
            <ListItemIcon {...provided.dragHandleProps}>
              <FormatAlignJustify color={'inherit'} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: accountTextSx }}>
              {accountName}
            </ListItemText>
            {rightButtonDisplayed ? (
              <ListItemSecondaryAction>
                {showLoading ? <CircularProgress size={22} /> : null}
                {showCloseButton ? (
                  <IconButton
                    aria-label={'close'}
                    edge={'end'}
                    onClick={onCloseButtonClicked}
                    size="large"
                  >
                    <Close />
                  </IconButton>
                ) : null}
                {showSaveError ? (
                  <IconButton aria-label={'error'} edge={'end'} size="large">
                    <ErrorOutline color={'error'} />
                  </IconButton>
                ) : null}
              </ListItemSecondaryAction>
            ) : null}
          </ListItemButton>
        </div>
      )}
    </Draggable>
  );
};
